import React, { useEffect, useState, useContext } from "react";
import SoftButton from "@components/common/SoftButton";
import { Dialog, DialogPanel } from "@headlessui/react";
import { navigate } from "gatsby";
import ProductSwiper from "@components/Home/Carousel/ProductSwiper";
import WideCard from "@components/productcards/WideCard";
import { MockCollections } from "@mockdata/mockdata";
import SearchBar from "@components/common/SearchBar";
import { ProductContext } from "@context/AllProductsContext";
import hanger from "@images/no-results-found.svg";
import { ArrowRight } from "@phosphor-icons/react";
import UseWindowSize from "@components/helpers/UseWindowSize";
import { sendGAEvent } from "@utils/analytics";

const MegaSearch = ({ isOpen = false, onClose }) => {
	const { width } = UseWindowSize()
	const [searchKeyword, setSearchKeyword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [searchResults, setSearchResults] = useState([]);
	const [searchResults20, setSearchResults20] = useState([]);
	const openState = isOpen || false;

	const { allProducts } = useContext(ProductContext);

	let recentlyViewed = [];

	if (typeof window !== "undefined") {
		let storedData = localStorage.getItem("recentlyViewed");
		try {
			recentlyViewed = storedData ? JSON.parse(storedData) : [];
		} catch (error) {
			recentlyViewed = [];
			localStorage.setItem("recentlyViewed", JSON.stringify([]));
		}
	}

	useEffect(() => {
		if (searchKeyword.trim().length < 3) return;

		setIsLoading(true);
		const debounceTimeout = setTimeout(() => {
			if (searchKeyword) {
				const keyword = searchKeyword?.toLowerCase().trim();
                const keywords = keyword?.split(/\s+/).filter(Boolean);

                const fullMatches = [];
                const partialMatches = [];
                const matchedHandles = new Set();

                //const EXCLUDED_COLLECTION = "bratz";

                for (const product of allProducts) {
                    const { title = "", handle = "", description = "", tags = [], collections = [] } = product;

                    
                    const collectionHandles = collections.map(col => col.handle);
                    //if (collectionHandles.includes(EXCLUDED_COLLECTION)) continue;

                    const searchableText = [
                        title,
                        handle,
                        description,
                        ...tags
                    ]
                    ?.filter(Boolean)
                    ?.join(" ")
                    ?.toLowerCase();
                    
                    if (searchableText.includes(keyword)) {
                        fullMatches.push(product);
                        matchedHandles.add(handle);
                        continue;
                    }
                    
                    const allWordsMatch = keywords.every((word) => searchableText.includes(word));
                    
                    if (allWordsMatch && !matchedHandles.has(handle)) {
                        partialMatches.push(product);
                        matchedHandles.add(handle);
                    }
                }
                
                const filteredProd = [...fullMatches, ...partialMatches];

				setSearchResults(filteredProd);
				setSearchResults20(filteredProd.slice(0, 20));
				setIsLoading(false);

                sendGAEvent('fuzzy_search', {search_term: searchKeyword})
			}
		}, 300); // Adjust debounce delay (300ms here)

		return () => clearTimeout(debounceTimeout); // Cleanup previous timeout
	}, [searchKeyword, allProducts]);

	useEffect(() => {
		// GA event for no results
		if (searchResults?.length === 0 && searchKeyword.trim() !== '') {
			sendGAEvent('no_result', {search_term: searchKeyword})
		} 
	}, [searchResults]);

	return (
		<React.Fragment>
			<Dialog
				open={openState}
				onClose={() => onClose()}
				className="relative z-[100]"
			>
				<div className="fixed flex justify-center top-10 inset-0 w-screen bg-black bg-opacity-60 overflow-hidden">
					<DialogPanel
						transition
						className="w-full max-container h-screen bg-white absolute z-50 top-0 px-4 shadow-md overflow-y-scroll scrollbar-hide mx-auto pb-20 lg:pb-28"
					>
						<div className="sticky flex flex-nowrap justify-between py-4 px-2 gap-x-3 text-tertiary">
							<SearchBar
								id={"megaSearchBar"}
								onChange={(e) => setSearchKeyword(e.target.value)}
								value={searchKeyword}
							/>
							<SoftButton
								label={"BACK"}
								onClick={onClose}
								ariaLabel={"Cancel Search"}
							/>
						</div>

						{searchResults.length <= 0 && searchKeyword !== "" ? (
							<section className="flex flex-col justify-center items-center gap-9 py-6">
								<img
									src={hanger}
									className="min-w-[244px] min-h-[66px]"
									alt="no results found"
								/>
								<div className="space-y-2 text-center">
									<h2 className="m-0">
										We couldn’t find any items matching your search
									</h2>
									<p>
										Try adjusting your filters or search terms, or browse all
										products to find what you’re looking for.
									</p>
								</div>

								<button
									className="btn-primary flex flex-nowrap gap-x-2 items-center"
									onClick={() => {
										navigate("/collections/all/");
									}}
									aria-label="Browse All Products"
								>
									Browse All Products
									<ArrowRight size={20} weight="light" />
								</button>
							</section>
						) : (
							<section className="grid grid-cols-1 py-4 space-y-8 lg:space-y-0">
								<div className="col-span-3 lg:col-span-2 space-y-8 lg:space-y-0">
									<div>
										<h6 className="font-normal flex items-center justify-between">
											{searchResults?.length === 0 || searchKeyword === "" ? (
												"RECENTLY VIEWED"
											) : (
												<>
													<span>{searchResults.length} RESULTS</span>
													<a
														href={`/search/?keyword=${searchKeyword}`}
														className="text-[#464646] flex items-center gap-1 border-[#464646] border-[1px] border-solid px-[10px] py-[8px]"
														aria-label="View all Search results"
														onClick={() => sendGAEvent('view_search_result', {search_term: searchKeyword})}
													>
														See All Results{" "}
														<ArrowRight size={20} weight="light" />
													</a>
												</>
											)}
										</h6>
										<section className="mt-4">
                                            {
                                                (searchResults?.length === 0 || searchKeyword === "") && recentlyViewed?.length === 0 ? 
                                                    <div className="pt-2 pb-6">No recent activity.</div>
                                                : null
                                            }
											<div
												className={`${
													isLoading && searchKeyword.trim() !== ""
														? "block"
														: "hidden"
												} text-center text-md my-20`}
											>
												<div className="loader mx-auto"></div>
												<div className="pt-2 pl-2">Loading...</div>
											</div>
											{width && (
												<ProductSwiper
													products={
														searchResults?.length === 0 || searchKeyword === ""
															? recentlyViewed
															: searchResults20
													}
													slidesPerView={4}
													spaceBetween={16}
													cardStyles={`w-20 lg:w-[262px]`}
													breakpoints={{
														// value of key is min-width property
														300: {
															slidesPerView: 1.5,
															spaceBetween: 5,
														},
														360: {
															slidesPerView: 1.9,
															spaceBetween: 5,
														},
														380: {
															slidesPerView: 2,
															spaceBetween: 5,
														},
														500: {
															slidesPerView: 2.5,
															spaceBetween: 10,
														},
														640: {
															slidesPerView: 3,
															spaceBetween: 10,
														},
														768: {
															slidesPerView: 3.5,
														},
														769: {
															slidesPerView: 2.5,
														},
														900: {
															slidesPerView: 3,
														},
														1024: {
															slidesPerView: 3.5,
														},
														1280: {
															slidesPerView: 4.5,
														},
														1440: {
															slidesPerView: 5,
														},
													}}
													width={
														width <= 420 ? "160" : width <= 768 ? "180" : "262"
													}
													height={
														width <= 420 ? "240" : width <= 768 ? "270" : "389"
													}
													loop
												/>
											)}
										</section>
									</div>

									<div>
										<h6 className="font-normal">POPULAR COLLECTIONS</h6>
										<section className="mt-4 w-full flex flex-nowrap flex-col md:flex-row gap-4">
											<WideCard
												data={MockCollections[4]}
												cardStyles={"max-w-none w-full lg:max-h-[260px]"}
											/>
											<WideCard
												data={MockCollections[5]}
												cardStyles={"max-w-none w-full lg:max-h-[260px]"}
											/>
										</section>
									</div>
								</div>
							</section>
						)}
					</DialogPanel>
				</div>
			</Dialog>
		</React.Fragment>
	);
};

export default MegaSearch;
